import { useMemo } from 'react';
import { Input } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Customer Master command header (Stage 6A.2-UX v2).
//
// One seamless instrument that sits ABOVE the frozen ErpDataGrid:
//   1. Command bar   — brandmark + title + live pills + search + Add/
//                      Import/Export. Search drives the SAME grid query
//                      as the (now hidden) grid toolbar search.
//   2. Exposure ribbon — the hero: total sanctioned credit + a stacked
//      utilisation bar (healthy / near / over / unused). Falls back to a
//      sanctioned-only framing when there are no live receivables yet.
//   3. Filter chips  — All / Active / On hold / GST / URP / Over-limit;
//      each applies a grid filter. Global counts come from summary.totals
//      / summary.exposure so a chip never reads 0 just because the grid
//      is currently filtered.
//
// Styling lives in CustomerMasterHeader.css (scoped `.cmx-*`); every
// colour is a Glass-i token literal — no ConfigProvider token override.
// ─────────────────────────────────────────────────────────────────

// Compact Indian-currency label: ₹1.86 Cr / ₹41.2 L / ₹8,500.
function inrShort(n) {
  const v = Math.abs(Number(n) || 0);
  if (v >= 1e7) return { rs: '₹', num: (v / 1e7).toFixed(2), unit: 'Cr' };
  if (v >= 1e5) return { rs: '₹', num: (v / 1e5).toFixed(2), unit: 'L' };
  if (v >= 1e3) return { rs: '₹', num: (v / 1e3).toFixed(1), unit: 'K' };
  return { rs: '₹', num: v.toLocaleString('en-IN'), unit: '' };
}

const CHIPS = [
  { key: 'all',     label: 'All',     dot: null,      read: (t) => t.total_count },
  { key: 'active',  label: 'Active',  dot: '#16A34A', read: (t) => t.active_count },
  { key: 'blocked', label: 'On hold', dot: '#DC2626', read: (t) => t.blocked_count },
  { key: 'gst',     label: 'GST',     dot: '#1256AE', read: (t) => t.gst_registered_count },
  { key: 'urp',     label: 'URP',     dot: null,      read: (t) => Math.max(0, t.total_count - t.gst_registered_count) }
];

export default function CustomerMasterHeader({
  totals,
  exposure,
  activeChip,
  onChipClick,
  searchValue,
  onSearchChange,
  onAdd,
  loading
}) {
  const t = useMemo(
    () => ({
      total_count:          Number(totals?.total_count)          || 0,
      active_count:         Number(totals?.active_count)         || 0,
      inactive_count:       Number(totals?.inactive_count)       || 0,
      blocked_count:        Number(totals?.blocked_count)        || 0,
      gst_registered_count: Number(totals?.gst_registered_count) || 0
    }),
    [totals]
  );
  const e = useMemo(
    () => ({
      sanctioned:       Number(exposure?.sanctioned)       || 0,
      used:             Number(exposure?.used)             || 0,
      used_within:      Number(exposure?.used_within)      || 0,
      over_amount:      Number(exposure?.over_amount)      || 0,
      with_limit_count: Number(exposure?.with_limit_count) || 0,
      healthy_count:    Number(exposure?.healthy_count)    || 0,
      near_count:       Number(exposure?.near_count)       || 0,
      over_count:       Number(exposure?.over_count)       || 0,
      has_receivables:  exposure?.has_receivables === true
    }),
    [exposure]
  );

  // Ribbon segments as % of sanctioned. When there are no receivables the bar
  // is one flat "sanctioned" track (all headroom) so it's never empty.
  const sanctioned = e.sanctioned || 0;
  const usedWithin = e.used_within || 0;
  const overAmt = e.over_amount || 0;
  // Split the "used within limit" figure by the healthy/near COUNT ratio so the
  // bar's green/amber split mirrors the legend without a second money query.
  const nearShare = e.near_count / Math.max(1, e.healthy_count + e.near_count);
  const nearMoney = usedWithin * nearShare;
  const healthyMoney = usedWithin - nearMoney;
  const denom = Math.max(1, sanctioned + overAmt);
  const pct = (x) => `${Math.max(0, (x / denom) * 100)}%`;
  const freeMoney = Math.max(0, sanctioned - usedWithin);

  const heroAmt = inrShort(sanctioned);

  return (
    <div className="cmx-head">
      {/* ── Command bar ── */}
      <div className="cmx-cmd">
        <span className="cmx-brandmk" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21h18M6 21V7l6-4 6 4v14M10 9h4M10 13h4M10 17h4" />
          </svg>
        </span>
        <div className="cmx-cmd__titles">
          <h1 className="cmx-cmd__h1">Customers</h1>
          <div className="cmx-cmd__pills">
            <span className="cmx-mpill"><b>{t.total_count.toLocaleString('en-IN')}</b> parties</span>
            <span className="cmx-mpill"><span className="cmx-mpill__d" style={{ background: '#16A34A' }} /><b>{t.active_count.toLocaleString('en-IN')}</b> active</span>
            {t.blocked_count > 0 ? (
              <span className="cmx-mpill"><span className="cmx-mpill__d" style={{ background: '#DC2626' }} /><b>{t.blocked_count.toLocaleString('en-IN')}</b> on hold</span>
            ) : null}
          </div>
        </div>

        <span className="cmx-cmd__spacer" />

        <Input
          allowClear
          value={searchValue}
          onChange={(ev) => onSearchChange(ev.target.value)}
          placeholder="Search name, GSTIN, mobile…"
          prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
          className="cmx-search"
          size="middle"
        />
        <button type="button" className="cmx-btn cmx-btn--primary" onClick={onAdd}>
          <PlusOutlined /> Add customer
        </button>
      </div>

      {/* ── Exposure ribbon (hero) ── */}
      <div className="cmx-ribbon">
        <div className="cmx-ribbon__lead">
          <div className="cmx-eyebrow">Credit exposure</div>
          <div className="cmx-ribbon__amt">
            <span className="cmx-ribbon__rs">{heroAmt.rs}</span>
            {loading ? '—' : heroAmt.num}
            {heroAmt.unit ? <span className="cmx-ribbon__unit">{heroAmt.unit}</span> : null}
          </div>
          <div className="cmx-ribbon__sub">
            sanctioned across <b>{e.with_limit_count.toLocaleString('en-IN')}</b> parties
          </div>
        </div>

        <div className="cmx-ribbon__barwrap">
          {e.has_receivables ? (
            <>
              <div className="cmx-expbar" role="img"
                   aria-label={`Credit utilisation: ${e.healthy_count} healthy, ${e.near_count} near limit, ${e.over_count} over limit`}>
                {healthyMoney > 0 ? <span className="cmx-expbar__s cmx-expbar__ok"   style={{ width: pct(healthyMoney) }} /> : null}
                {nearMoney > 0    ? <span className="cmx-expbar__s cmx-expbar__warn" style={{ width: pct(nearMoney) }} /> : null}
                {overAmt > 0      ? <span className="cmx-expbar__s cmx-expbar__over" style={{ width: pct(overAmt) }} /> : null}
                {freeMoney > 0    ? <span className="cmx-expbar__s cmx-expbar__free" style={{ width: pct(freeMoney) }} /> : null}
              </div>
              <div className="cmx-leg">
                <span className="cmx-leg__i"><span className="cmx-leg__sw" style={{ background: '#16A34A' }} />Healthy <b>{e.healthy_count}</b></span>
                <span className="cmx-leg__i"><span className="cmx-leg__sw" style={{ background: '#F59E0B' }} />Near limit <b>{e.near_count}</b></span>
                <span className="cmx-leg__i"><span className="cmx-leg__sw" style={{ background: '#DC2626' }} />Over limit <b>{e.over_count}</b></span>
                <span className="cmx-leg__i cmx-leg__i--muted"><span className="cmx-leg__sw cmx-leg__sw--free" />Unused {inrShort(freeMoney).rs}{inrShort(freeMoney).num}{inrShort(freeMoney).unit}</span>
              </div>
            </>
          ) : (
            <>
              <div className="cmx-expbar">
                <span className="cmx-expbar__s cmx-expbar__free" style={{ width: '100%' }} />
              </div>
              <div className="cmx-leg">
                <span className="cmx-leg__i cmx-leg__i--muted">
                  No posted receivables yet — the bar fills as invoices are raised.{' '}
                  <b>{e.with_limit_count}</b> parties have a credit limit set.
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Filter chips ── */}
      <div className="cmx-chips" role="group" aria-label="Filter customers">
        {CHIPS.map((chip) => {
          const value = chip.read(t, e);
          const isOn = activeChip === chip.key;
          return (
            <button
              type="button"
              key={chip.key}
              className={`cmx-chip${isOn ? ' cmx-chip--on' : ''}`}
              aria-pressed={isOn}
              onClick={() => onChipClick(isOn ? 'all' : chip.key)}
            >
              {chip.dot ? <span className="cmx-chip__d" style={{ background: chip.dot }} /> : null}
              <span className="cmx-chip__n">{loading ? '—' : value.toLocaleString('en-IN')}</span>
              {chip.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
